import { IntegrationSystem, SelectOption } from '@/shared/types';

import type { DeclaredDocumentsFormDataProps } from '@/components/Documents/DeclaredDocumentsCreate';

export enum AddressFieldName {
  country = 'country',
  region = 'region',
  district = 'district',
  settlement = 'settlement',
  street = 'street',
  house = 'house',
  building = 'building',
  apartment = 'apartment',
  zip_code = 'zip_code',
}

export interface StreetSelectOption extends SelectOption {
  postCode: Nullable<string>;
}

export interface FormAddress {
  [AddressFieldName.country]: Nullable<SelectOption>;
  [AddressFieldName.region]: Nullable<SelectOption>;
  [AddressFieldName.district]: Nullable<SelectOption>;
  [AddressFieldName.settlement]: Nullable<SelectOption | string>;
  [AddressFieldName.street]: Nullable<StreetSelectOption | string>;
  [AddressFieldName.house]: Nullable<string>;
  [AddressFieldName.building]: Nullable<string>;
  [AddressFieldName.apartment]: Nullable<string>;
  [AddressFieldName.zip_code]: Nullable<string>;
}

export enum DocumentFieldName {
  type = 'type',
  attachments = 'attachments',
  serialNumberDoc = 'serialNumberDoc',
  indexNumber = 'indexNumber',
  auth_dep = 'auth_dep',
  date = 'date',
  expirationDate = 'expirationDate',
  unzr = 'unzr',
  org = 'org',
}

export interface FormDocument<System extends IntegrationSystem = IntegrationSystem.ERZ> {
  [DocumentFieldName.type]?: DocumentTypeEntity<System>;
  info?: Partial<Record<Exclude<DocumentFieldName, DocumentFieldName.type | DocumentFieldName.attachments>, any>>;
  [DocumentFieldName.attachments]?: File[];
}

export interface Attachment {
  file_name: string;
  content: File;
}

export enum DocumentRegisteredDocDataFieldName {
  registeredDocType = 'registeredDocType',
  registeredLocalCommunityNumber = 'registeredLocalCommunityNumber',
  registeredLocalCommunityDate = 'registeredLocalCommunityDate',
  registeredLocalCommunityAuthDep = 'registeredLocalCommunityAuthDep',
  registeredDocumentLocalCommunityDoc = 'registeredDocumentLocalCommunityDoc',
}

export interface SavedAttachment {
  uuid: string;
  file_name: string;
  mime_type: string;
  form_field: string;
  document_type: Nullable<DocumentTypeCode>;
  link: Nullable<string>;
  encoded_content: Nullable<string>;
  base_file?: File;
}

export type DocumentData = {
  id?: number;
  type: Nullable<DocumentTypeCode>;
  addressType?: AddressType;
  fields: {
    typeName?: string;
    referenceNumber?: string;
    issue?: string;
    dateIssue?: string;
    expiredDate?: string;
    issueCountryIso2?: string;
    date?: string;
    auth_dep?: string;
    expirationDate?: string;
    unzr?: string;

    [DocumentRegisteredDocDataFieldName.registeredLocalCommunityNumber]?: string;
    [DocumentRegisteredDocDataFieldName.registeredLocalCommunityDate]?: string;
    [DocumentRegisteredDocDataFieldName.registeredLocalCommunityAuthDep]?: string;
  } & DeclaredDocumentsFormDataProps;
};

export enum AddressType {
  LIVING = 'LIVING',
  REGISTRATION = 'REGISTRATION',
  BIRTH = 'BIRTH',
}

export enum AddressSource {
  BOTH = 'ABROAD',
  MANUAL = 'MANUAL',
  REGISTER = 'REGISTER',
}

interface AddressDataBaseType {
  type: AddressType;
  source: AddressSource;
  document_id: number;
  state: Nullable<string>;
}

export interface LivingAddress extends AddressDataBaseType, FormAddress {
  type: AddressType.LIVING;
}

export interface RegisteredAddress extends AddressDataBaseType, FormAddress {
  type: AddressType.REGISTRATION;
}

export interface BirthAddress extends AddressDataBaseType, Record<keyof FormAddress, Nullable<string>> {
  type: AddressType.BIRTH;
}

export type UserAddress = LivingAddress | RegisteredAddress | BirthAddress;

export type IACDocumentTypeCode =
  | DocumentTypeCode.PASSPORT
  | DocumentTypeCode.ID_CARD
  | DocumentTypeCode.INTERNATIONAL_PASSPORT
  | DocumentTypeCode.RESIDENCE;

export enum DocumentTypeCode {
  LEASE_AGREEMENT = 'lease_agreement',
  OWNERSHIP_CERTIFICATE = 'ownership_certificate',
  VITYAG_TERITORIALNOYI_GROMADI = 'address',
  LEGAL_RESIDENCE = 'legal_residence',
  TEMPORARY_RESIDENCE = 'temporary_residence',
  CERT_IDP = 'cert_idp',
  CERT_MARRIAGE = 'cert_marriage',
  EXRACT_PRPR = 'exract_prpr',
  PAYMENT_REFERENCE = 'payment_reference',
  MEDICAL_REFERENCE = 'medical_reference',
  INSURANCE_POLICY = 'insurance_policy',
  WORK_REFERENCE = 'work_reference',
  TECHNICAL_REFERENCE = 'technical_reference',
  POWER_OF_ATTORNEY = 'power_of_attorney',
  TECHNICAL_CONDITION = 'technical_condition',
  INVITE = 'invite',
  INVITE_UKR = 'invite_ukr',
  RESOLUTION = 'resolution',
  PERMIT = 'permit',
  CERTIFICATION = 'certification',
  RNOKPP = 'rnokpp',
  PHOTO_CARD = 'photo_card',
  MISSING_PERSON_PHOTO_CARD = 'missing_person_photo_card',
  MISSIN_PERSON_ADDITIONAL_PERSONAL_INFO = 'missing_person_additional_personal_documents',
  PASSPORT = 'passport',
  ID_CARD = 'id_card',
  INTERNATIONAL_PASSPORT = 'international_passport',
  RESIDENCE = 'residency',
  OLD_RESIDENCE = 'old_residency',
  ADDITIONAL_DOCUMENT = 'additional_document',
  DISAPPEARANCE_MISSING_PERSON = 'disappearance_missing_person',
  EXTRACT_ERDR = 'extract_erdr',
  ANCESTRAL_CONNECTION = 'ancestral_connection',
  WEAPON_LEFT_PHOTO = 'weapon_left_photo',
  WEAPON_RIGHT_PHOTO = 'weapon_right_photo',
  EXPERT_RESEARCH = 'expert_research',
  APPLICATION = '0101',
  OTHER = 'other',
  CRIMINAL_RECORD = 'criminal record',

  COMMISSION_SALE = 'application_IVE01',
  PURCHASE = 'application_IBB01',
  TRANSPORT_THROUGH_UKRAINE = 'application_ITT01',
  IMPORT_IN_UKRAINE = 'application_ITT02',
  TRANSPORT_ACROSS_BORDER = 'application_ITT05',
  COLD_STEEL_TRANSPORTATION = 'application_ITT06',
  STORAGE_CARRYING = 'application_ISC01',
  COLD_STEEL_STORAGE_FOR_COLLECTION = 'application_ICS01',
  RESIDENCE_RELOCATION = 'application_IAC01',
  TEMPORARY_RESIDENCE_RELOCATION = 'application_IAC02',
  VALIDITY_PERIOD_PROLONGATION = 'application_extension',
  MISSING_PERSON = 'application_missingPerson',
  MISSING_PERSON_EXTRACT = 'application_RMP02',
  REAPPLICATION = 'application_reapplication',

  CONTRACT_SECURITY_ACTIVITIES = 'contract_security_activities',
  LEARNING_DOCUMENT = 'learning_document',

  WEAPON_TECHNICAL_REFERENCE = 'weapon_technical_reference',
}

export interface DocumentType<System extends IntegrationSystem = never> {
  id: string;
  kind: System extends IntegrationSystem.ERZ ? 'A' | 'B' | 'C' | 'D' | 'E' | 'F' : never;
  name: string;
  code: System extends IntegrationSystem.ERZ
    ? DocumentTypeCode
    : System extends IntegrationSystem.IAC
      ? IACDocumentTypeCode
      : never;
}

export interface DocumentTypeEntity<T extends IntegrationSystem = never> extends SelectOption {
  code: DocumentType<T>['code'] | 'null';
}

export type FormDataType = Record<
  string,
  Nullable<
    | string
    | number
    | File[]
    | string[]
    | boolean
    | Date
    | FormDocument[]
    | Attachment[]
    | FormAddress
    | Record<string, unknown>
    | SavedAttachment[]
  >
>;

export interface AddressPayload {
  type: AddressType;
  country: string;
  region: string;
  district: string;
  settlement: string;
  street: string;
  house: string;
  building: string;
  apartment: string;
  source: string;
  document_id?: string;
}
